export interface CostStats {
  totalApiCalls: number;
  totalTokens: number;
  estimatedCost: number;
  cacheSavings: number;
  costPerRequest: number;
  costTrend: 'up' | 'down' | 'stable';
}

export interface OptimizationSuggestion {
  type: 'cache' | 'batch' | 'model' | 'timeout';
  priority: 'high' | 'medium' | 'low';
  description: string;
  estimatedSavings: number;
}

class CostOptimizer {
  private apiCallCount = 0;
  private tokenUsage = 0;
  private startTime = Date.now();
  private costHistory: number[] = [];
  private cacheHits = 0;
  private cacheMisses = 0;

  // 价格配置（元/千token）
  private pricing = {
    input: 0.001,
    output: 0.002,
  };

  recordApiCall(tokens: { input: number; output: number }): void {
    this.apiCallCount++;
    this.tokenUsage += tokens.input + tokens.output;
    
    // 记录当前成本
    const currentCost = this.calculateCurrentCost();
    this.costHistory.push(currentCost);
    
    if (this.costHistory.length > 100) {
      this.costHistory.shift();
    }
  }

  recordCacheResult(isHit: boolean): void {
    if (isHit) {
      this.cacheHits++;
    } else {
      this.cacheMisses++;
    }
  }

  getCostStats(): CostStats {
    const totalRequests = this.cacheHits + this.cacheMisses + this.apiCallCount;
    const cacheHitRate = totalRequests > 0 ? (this.cacheHits / totalRequests) * 100 : 0;
    
    const estimatedCost = this.calculateCurrentCost();
    const potentialSavings = (this.cacheHits * (this.pricing.input + this.pricing.output)) / 1000;
    
    let costTrend: 'up' | 'down' | 'stable' = 'stable';
    if (this.costHistory.length >= 10) {
      const recent = this.costHistory.slice(-10);
      const firstHalf = recent.slice(0, 5).reduce((a, b) => a + b, 0) / 5;
      const secondHalf = recent.slice(5).reduce((a, b) => a + b, 0) / 5;
      
      if (secondHalf > firstHalf * 1.1) {
        costTrend = 'up';
      } else if (secondHalf < firstHalf * 0.9) {
        costTrend = 'down';
      }
    }

    return {
      totalApiCalls: this.apiCallCount,
      totalTokens: this.tokenUsage,
      estimatedCost: Math.round(estimatedCost * 1000) / 1000,
      cacheSavings: Math.round(potentialSavings * 1000) / 1000,
      costPerRequest: totalRequests > 0 ? estimatedCost / totalRequests : 0,
      costTrend,
    };
  }

  getOptimizationSuggestions(): OptimizationSuggestion[] {
    const suggestions: OptimizationSuggestion[] = [];
    const cacheHitRate = (this.cacheHits + this.cacheMisses) > 0 
      ? (this.cacheHits / (this.cacheHits + this.cacheMisses)) * 100 
      : 0;

    if (cacheHitRate < 50) {
      suggestions.push({
        type: 'cache',
        priority: 'high',
        description: '缓存命中率较低，建议增加缓存时间或优化缓存键生成策略',
        estimatedSavings: this.tokenUsage * (this.pricing.input + this.pricing.output) / 1000 * 0.3,
      });
    }

    if (this.apiCallCount > 100) {
      suggestions.push({
        type: 'batch',
        priority: 'medium',
        description: 'API调用频繁，建议考虑批量处理请求以减少开销',
        estimatedSavings: this.apiCallCount * 0.01,
      });
    }

    const stats = this.getCostStats();
    if (stats.costTrend === 'up') {
      suggestions.push({
        type: 'model',
        priority: 'high',
        description: '成本呈上升趋势，建议考虑使用更轻量的模型或优化提示词',
        estimatedSavings: stats.estimatedCost * 0.2,
      });
    }

    return suggestions;
  }

  private calculateCurrentCost(): number {
    // 简化的成本计算
    const inputTokens = Math.floor(this.tokenUsage * 0.6);
    const outputTokens = Math.floor(this.tokenUsage * 0.4);
    return (inputTokens * this.pricing.input + outputTokens * this.pricing.output) / 1000;
  }

  exportCostReport(): string {
    const stats = this.getCostStats();
    const suggestions = this.getOptimizationSuggestions();

    let report = `=== 成本优化报告 ===\n`;
    report += `生成时间: ${new Date().toLocaleString()}\n`;
    report += `运行时长: ${Math.round((Date.now() - this.startTime) / 1000)}秒\n\n`;
    report += `API调用次数: ${stats.totalApiCalls}\n`;
    report += `Token使用量: ${stats.totalTokens}\n`;
    report += `预估成本: ¥${stats.estimatedCost}\n`;
    report += `缓存节省: ¥${stats.cacheSavings}\n`;
    report += `成本趋势: ${stats.costTrend}\n\n`;
    report += `优化建议:\n`;
    
    suggestions.forEach((sugg, idx) => {
      report += `${idx + 1}. [${sugg.priority.toUpperCase()}] ${sugg.description}\n`;
      report += `   预估节省: ¥${Math.round(sugg.estimatedSavings * 1000) / 1000}\n`;
    });

    return report;
  }

  reset(): void {
    this.apiCallCount = 0;
    this.tokenUsage = 0;
    this.costHistory = [];
    this.cacheHits = 0;
    this.cacheMisses = 0;
    this.startTime = Date.now();
  }
}

export const costOptimizer = new CostOptimizer();
